import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { javascript } from '@codemirror/lang-javascript';
import { java } from '@codemirror/lang-java';
import { cpp } from '@codemirror/lang-cpp';
import { indentUnit } from '@codemirror/language';
import { PROBLEM, STARTERS, LANGS, buildDsaPrompt } from './dsaProblem';
import { DSA_TOOLS } from './dsaTools';
import { renderMarkdown } from './markdown';
import APIService from '../../services/APIService';
import { markSolved } from './dsaProgress';
import { logDsaAttempt, logDsaSolved, logDsaTool } from './memoryLog';
import {
  IconSpark, IconSend, IconStop, IconMaximize, IconMinimize, IconCode,
  IconArrowLeft, IconArrowRight, IconClock, IconCheck, IconX, IconChevRight,
} from './icons';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// CodeMirror language grammar per editor language.
const LANG_EXT = { Python3: python, JavaScript: javascript, Java: java, 'C++': cpp };
const langExtension = (l) => (LANG_EXT[l] || python)();
const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const uid = (p) => `${p}-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

/*
 * DsaWorkspace — the FULL-SCREEN DSA mode (replaces the chat-thread view used
 * by other modes). Layout:
 *   ┌ topbar (problem title · tags · fullscreen) ─────────────────────────┐
 *   │ LEFT  (problem ‖ editor ‖ tests + Run/Submit)  │  RIGHT (AI chat)    │
 *   └──────────────────────────────────────────────────────────────────────┘
 * The right panel is a self-contained streaming chat scoped to the current
 * problem + code. Its "tools" are declared in dsaTools.js; each maps a
 * structured intent → a prompt sent to /chat (documented in docs/DSA_CHAT.md).
 * `registerSend` lets the sidebar's Related/Try-next links post into this chat.
 */
const DsaWorkspace = ({ config, registerSend, onBack, onOpenQuestion, questionId = 3 }) => {
  // The problem is fetched from the backend (dsa_service) by question_id.
  const [question, setQuestion] = useState(null);
  const [loadingQ, setLoadingQ] = useState(true);
  const [related, setRelated] = useState(null);   // { next, prev, similar }

  // Normalize the backend question into the shape the UI/tools expect; fall
  // back to the bundled PROBLEM until the fetch resolves.
  const problem = useMemo(() => {
    if (!question) return PROBLEM;
    const examples = (question.examples || []).map((e, i) => ({
      name: `Case ${i + 1}`, input: e.input, output: e.output, expected: e.output,
    }));
    return {
      num: question.question_id,
      title: question.title,
      difficulty: question.difficulty,
      tags: question.tags || [],
      statement: question.problem_description || '',
      examples,
      testcases: examples,
    };
  }, [question]);

  // One code buffer per language, so switching languages keeps what you wrote
  // in each and seeds the right starter template.
  const [codeByLang, setCodeByLang] = useState(() => ({ ...STARTERS }));
  const [lang, setLang] = useState('Python3');
  const code = codeByLang[lang];
  const setCode = useCallback((val) => setCodeByLang((prev) => ({ ...prev, [lang]: val })), [lang]);

  const [activeCase, setActiveCase] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  // The assistant docks away to a slim rail — preference survives reloads.
  const [assistOpen, setAssistOpen] = useState(() => localStorage.getItem('dsaws_assist') !== 'closed');
  const toggleAssist = () => setAssistOpen((o) => {
    localStorage.setItem('dsaws_assist', o ? 'closed' : 'open');
    return !o;
  });
  const [submitResult, setSubmitResult] = useState(null);
  const [judging, setJudging] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);   // seconds on this question

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const endRef = useRef(null);
  const codeRef = useRef(code);
  const langRef = useRef(lang);
  const problemRef = useRef(problem);
  codeRef.current = code;
  langRef.current = lang;
  problemRef.current = problem;

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; abortRef.current?.abort(); }; }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Fetch the problem by question_id, and seed the Python editor with its
  // starter code (only while the buffer is still the untouched default).
  useEffect(() => {
    let active = true;
    setLoadingQ(true);
    setActiveCase(0);
    setSubmitResult(null);
    APIService.getDsaQuestion(questionId)
      .then((q) => {
        if (!active) return;
        setQuestion(q);
        if (q.starter_code) {
          setCodeByLang((prev) => (
            prev.Python3 === STARTERS.Python3 ? { ...prev, Python3: q.starter_code } : prev
          ));
        }
      })
      .catch(() => { /* keep the bundled PROBLEM fallback */ })
      .finally(() => { if (active) setLoadingQ(false); });
    return () => { active = false; };
  }, [questionId]);

  // Next / previous / similar problems for this question.
  useEffect(() => {
    let active = true;
    setRelated(null);
    APIService.getDsaRelated(questionId)
      .then((r) => { if (active) setRelated(r); })
      .catch(() => { /* related nav is optional */ });
    return () => { active = false; };
  }, [questionId]);

  // Per-question timer — starts fresh whenever the question changes.
  useEffect(() => {
    setElapsed(0);
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [questionId]);

  // Stream /chat tokens into message `aiId`.
  const streamInto = useCallback(async (aiId, promptText) => {
    setStreaming(true);
    abortRef.current = new AbortController();
    try {
      const resp = await fetch(`${API_BASE_URL}/chat?q=${encodeURIComponent(promptText)}`, { signal: abortRef.current.signal });
      if (!resp.ok || !resp.body) throw new Error(`Request failed (${resp.status})`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          let token;
          try { token = JSON.parse(payload); } catch { token = payload; }
          if (mountedRef.current) setMessages((prev) => prev.map((m) => (m.id === aiId ? { ...m, content: m.content + token } : m)));
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError' && mountedRef.current) {
        setMessages((prev) => prev.map((m) => (m.id === aiId
          ? { ...m, content: m.content || '⚠️ Could not reach the AI. Is the backend running?', error: true } : m)));
      }
    } finally {
      if (mountedRef.current) {
        setMessages((prev) => prev.map((m) => (m.id === aiId ? { ...m, streaming: false } : m)));
        setStreaming(false);
      }
    }
  }, []);

  // Core action: post a labelled user turn, then stream the AI reply. The
  // prompt is always wrapped with the live problem + code context.
  const ask = useCallback((userPrompt, label) => {
    if (streaming) return;
    setMessages((prev) => [...prev, { role: 'user', content: label || userPrompt, time: now() }]);
    const aiId = uid('ai');
    setMessages((prev) => [...prev, { role: 'ai', content: '', id: aiId, streaming: true, time: now() }]);
    streamInto(aiId, buildDsaPrompt(userPrompt, codeRef.current, langRef.current, problemRef.current));
  }, [streaming, streamInto]);

  // Invoke a declared tool by key (chips + Run + sidebar links use this).
  const invokeTool = useCallback((key, args = {}) => {
    const tool = DSA_TOOLS.find((t) => t.key === key);
    if (!tool) return;
    logDsaTool(questionId, key, problemRef.current?.tags || []);
    ask(tool.buildPrompt(args, { problem: problemRef.current, activeCase }), tool.label);
  }, [ask, activeCase, questionId]);

  const sendChat = useCallback((raw) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    setInput('');
    ask(text, text);
  }, [input, streaming, ask]);

  // Expose the chat to the sidebar (Related / Try-next links → this panel).
  const sendRef = useRef(sendChat);
  sendRef.current = sendChat;
  useEffect(() => {
    registerSend?.((t) => sendRef.current?.(t));
    return () => registerSend?.(null);
  }, [registerSend]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }
  };

  // Run → execute against the SAMPLE tests (fast, per-case + time).
  const run = useCallback(async () => {
    if (running || judging) return;
    setRunning(true);
    setSubmitResult(null);
    try {
      const res = await APIService.runDsa(questionId, langRef.current, codeRef.current);
      if (mountedRef.current) setSubmitResult({ ...res, _kind: 'run' });
    } catch (e) {
      if (mountedRef.current) setSubmitResult({ verdict: 'error', passed: false, message: e.message || 'Run failed.', _kind: 'run' });
    } finally {
      if (mountedRef.current) setRunning(false);
    }
  }, [running, judging, questionId]);

  // Submit → run the code against ALL hidden tests (dsa_backend / Judge0).
  const submit = useCallback(async () => {
    if (judging || running) return;
    setJudging(true);
    setSubmitResult(null);
    try {
      const res = await APIService.submitDsa(questionId, langRef.current, codeRef.current);
      // Record every submission attempt (pass or fail), plus a solve on accept.
      const tags = problem?.tags || [];
      logDsaAttempt(questionId, tags, res.passed, res.verdict || (res.passed ? 'accepted' : 'failed'));
      if (res.passed) {
        markSolved(questionId);
        logDsaSolved(questionId, tags);
      }
      if (mountedRef.current) setSubmitResult({ ...res, _kind: 'submit' });
    } catch (e) {
      if (mountedRef.current) setSubmitResult({ verdict: 'error', passed: false, message: e.message || 'Submission failed.', _kind: 'submit' });
    } finally {
      if (mountedRef.current) setJudging(false);
    }
  }, [judging, running, questionId]);

  const emptyChat = messages.length === 0;
  const verdictLabel = submitResult && (submitResult.passed ? 'Accepted' : (submitResult.verdict || 'failed').replace(/_/g, ' '));
  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className={`nano-dsaws${fullscreen ? ' is-fullscreen' : ''}`}>
      {/* Topbar */}
      <div className="nano-dsaws-topbar">
        <div className="nano-dsaws-titlewrap">
          {onBack && (
            <button className="nano-dsaws-back" onClick={onBack} title="All problems" type="button">
              <IconArrowLeft size={16} />
            </button>
          )}
          <IconCode size={17} />
          <div>
            <div className="nano-dsaws-title">{problem.num}. {problem.title}</div>
            <div className="nano-dsa-tags">
              <span className="nano-dsa-tag diff">{problem.difficulty}</span>
              {(problem.tags || []).map((t) => <span key={t} className="nano-dsa-tag">{t}</span>)}
            </div>
          </div>
        </div>
        <div className="nano-dsaws-topbar-actions">
          <span className="nano-dsaws-timer" title="Time on this problem"><IconClock size={14} /> {fmtTime(elapsed)}</span>
          {related?.next && onOpenQuestion && (
            <button className="nano-dsaws-next" onClick={() => onOpenQuestion(related.next.question_id)} type="button" title={`Next: ${related.next.title}`}>
              Next <IconArrowRight size={15} />
            </button>
          )}
          <button className="nano-dsaws-icon-btn" onClick={() => setFullscreen((f) => !f)} title={fullscreen ? 'Exit full screen' : 'Full screen'} type="button">
            {fullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
          </button>
        </div>
      </div>

      {/* Body: left IDE · right chat */}
      <div className="nano-dsaws-body">
        {/* LEFT — problem / editor / tests (always side-by-side) */}
        <div className="nano-dsaws-ide split-row">
          {/* Problem (always visible) */}
          <div className="nano-dsaws-problem">
            <div className="nano-dsaws-problem-head">
              <span>Problem</span>
              <span className="nano-dsaws-qid">#{problem.num}</span>
            </div>
            <div className="nano-dsaws-problem-body">
              {loadingQ && !question ? (
                <p className="nano-dsa-stmt">Loading problem…</p>
              ) : (
                <div className="nano-dsa-desc">{problem.statement}</div>
              )}
              {/* Static fallback keeps its structured examples/constraints. */}
              {!question && PROBLEM.examples.map((ex, i) => (
                <div key={i} className="nano-dsa-example">
                  <div className="nano-dsa-ex-h">Example {i + 1}:</div>
                  <div className="nano-dsa-ex-box">
                    <div><b>Input:</b> {ex.input}</div>
                    <div><b>Output:</b> {ex.output}</div>
                    {ex.explanation && <div><b>Explanation:</b> {ex.explanation}</div>}
                  </div>
                </div>
              ))}

              {/* Similar problems (tag-based) */}
              {related?.similar?.length > 0 && (
                <div className="nano-dsaws-similar">
                  <div className="nano-dsaws-similar-h">Similar Problems</div>
                  {related.similar.map((s) => (
                    <button key={s.question_id} className="nano-dsaws-similar-row"
                      onClick={() => onOpenQuestion?.(s.question_id)} type="button">
                      <span className="s-title">{s.question_id}. {s.title}</span>
                      <span className={`nano-dsa-tag diff-${(s.difficulty || '').toLowerCase()}`}>{s.difficulty}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Editor + tests */}
          <div className="nano-dsaws-editorwrap">
            <div className="nano-dsa-editor-head">
              <span className="nano-dsa-panel-lbl">Code Editor</span>
              <select className="nano-dsa-lang" value={lang} onChange={(e) => setLang(e.target.value)}>
                {LANGS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div className="nano-dsaws-code">
              <CodeMirror
                value={code}
                onChange={setCode}
                height="100%"
                theme="dark"
                className="nano-cm"
                extensions={[langExtension(lang), indentUnit.of('    ')]}
                basicSetup={{ lineNumbers: true, highlightActiveLine: true, autocompletion: false }}
              />
            </div>
            {problem.testcases && problem.testcases.length > 0 && (
              <div className="nano-dsaws-tests">
                <div className="nano-dsa-case-tabs">
                  {problem.testcases.slice(0, 6).map((tc, i) => (
                    <button key={tc.name} className={`nano-dsa-case${activeCase === i ? ' active' : ''}`} onClick={() => setActiveCase(i)} type="button">{tc.name}</button>
                  ))}
                </div>
                <div className="nano-dsa-case-io">
                  <span className="lbl">Input</span>
                  <span className="box">{problem.testcases[activeCase]?.input}</span>
                  <span className="lbl">Expected</span>
                  <span className="box">{problem.testcases[activeCase]?.expected}</span>
                </div>
              </div>
            )}

            {/* Run / Submit verdict — graceful, with passed count + time */}
            {submitResult && (
              <div className={`nano-dsaws-verdict ${submitResult.passed ? 'ok' : 'fail'}`}>
                <div className="v-head">
                  <span className="v-dot" />
                  <span className="v-label">
                    {submitResult._kind === 'run' ? (submitResult.passed ? 'Sample tests passed' : 'Sample run') : verdictLabel}
                  </span>
                  {(submitResult.total_tests > 0) && (
                    <span className="v-count">{submitResult.passed_count ?? 0} / {submitResult.total_tests} passed</span>
                  )}
                  {(submitResult.time_ms != null) && <span className="v-meta"><IconClock size={12} /> {submitResult.time_ms} ms</span>}
                  {submitResult.runner && <span className="v-meta">· {submitResult.runner}</span>}
                </div>
                <div className="v-msg">{submitResult.message}</div>

                {/* Per-case chips for a sample Run */}
                {submitResult._kind === 'run' && submitResult.cases?.length > 0 && (
                  <div className="v-cases">
                    {submitResult.cases.map((c) => (
                      <span key={c.i} className={`v-case ${c.passed ? 'ok' : 'no'}`} title={c.error || 'Passed'}>
                        {c.passed ? <IconCheck size={11} /> : <IconX size={11} />} Case {c.i}
                      </span>
                    ))}
                  </div>
                )}

                {/* Graceful error (first failing case), never a raw traceback */}
                {!submitResult.passed && submitResult.error_detail && (
                  <div className="v-err">{submitResult.error_detail}</div>
                )}
              </div>
            )}

            <div className="nano-dsaws-editor-actions">
              <button className="nano-dsa-run" onClick={run} disabled={running || judging} type="button">
                {running ? 'Running…' : 'Run'}
              </button>
              <button className="nano-dsa-submit" onClick={submit} disabled={judging || running} type="button">
                {judging ? 'Judging…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — docked AI chat (collapsible to a slim rail) */}
        {!assistOpen ? (
          <button className="nano-dsaws-chat-rail" onClick={toggleAssist} type="button"
            title="Open the AI Assistant">
            <IconSpark size={18} />
            <span className="rail-label">AI Assistant</span>
          </button>
        ) : (
        <div className="nano-dsaws-chat">
          <div className="nano-dsaws-chat-head">
            <div className="nano-dsaws-chat-headrow">
              <div className="nano-dsaws-chat-title"><IconSpark size={15} /> AI Assistant</div>
              <button className="nano-dsaws-icon-btn" onClick={toggleAssist} type="button"
                title="Hide the assistant — more room to code">
                <IconChevRight size={15} />
              </button>
            </div>
            <span className="nano-dsaws-chat-sub">Knows this problem &amp; your code</span>
          </div>

          {/* Tool chips */}
          <div className="nano-dsaws-tools">
            {DSA_TOOLS.filter((t) => t.chip).map((t) => (
              <button key={t.key} className="nano-dsa-chip" onClick={() => invokeTool(t.key)} disabled={streaming} type="button" title={t.description}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="nano-dsaws-msgs">
            {emptyChat && (
              <div className="nano-dsaws-empty">
                <div className="nano-dsaws-empty-orb"><IconSpark size={22} /></div>
                <p>Ask anything about this problem, or tap a tool above — <b>Hint</b>, <b>Explain</b>, <b>Optimize</b>, <b>Debug</b>. I can see your code and the test cases.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || i} className={`nano-dsaws-msg ${m.role}`}>
                {m.role === 'ai' ? (
                  <div className={`nano-bubble${m.error ? ' error' : ''}`}>
                    {renderMarkdown(m.content)}{m.streaming && <span className="nano-cursor" />}
                  </div>
                ) : (
                  <div className="nano-dsaws-userbubble">{m.content}</div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <div className="nano-dsaws-composer">
            <textarea
              placeholder="Ask about this problem…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
            />
            {streaming ? (
              <button className="nano-send stop" onClick={() => abortRef.current?.abort()} title="Stop" type="button"><IconStop size={16} /></button>
            ) : (
              <button className="nano-send" onClick={() => sendChat()} disabled={!input.trim()} title="Send" type="button"><IconSend size={16} /></button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default DsaWorkspace;
